import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  RefreshCw, ChevronDown, ChevronRight, Check, RotateCcw, X,
  Users, AlertTriangle, Clock, CheckCircle, DollarSign, Filter,
  Paperclip, FileText, Image as ImageIcon, Upload, Calendar,
  Download, ExternalLink, Send
} from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'
import { contem } from '../../utils/texto'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—'

const STATUS_CONFIG = {
  em_aberto: { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-700', icon: Clock },
  atrasado:  { label: 'Atrasado', cls: 'bg-red-100 text-red-700',    icon: AlertTriangle },
  pago:      { label: 'Repassado', cls: 'bg-green-100 text-green-700', icon: CheckCircle },
}

// ── Seletor de Período ────────────────────────────────────────────────────────
const iso = (d) => d.toISOString().split('T')[0]

const PERIODOS = [
  { value: 'todos',      label: 'Todos os períodos' },
  { value: 'hoje',       label: 'Hoje' },
  { value: 'semana',     label: 'Esta semana' },
  { value: 'mes',        label: 'Este mês' },
  { value: 'mes_passado', label: 'Mês passado' },
  { value: 'proximos30', label: 'Próximos 30 dias' },
  { value: 'ano',        label: 'Este ano' },
  { value: 'custom',     label: 'Personalizado' },
]

function calcRange(key) {
  const today = new Date()
  switch (key) {
    case 'hoje': return { inicio: iso(today), fim: iso(today) }
    case 'semana': {
      const start = new Date(today); const diff = today.getDay() === 0 ? -6 : 1 - today.getDay()
      start.setDate(today.getDate() + diff); return { inicio: iso(start), fim: iso(today) }
    }
    case 'mes': return { inicio: iso(new Date(today.getFullYear(), today.getMonth(), 1)), fim: iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)) }
    case 'mes_passado': return { inicio: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)), fim: iso(new Date(today.getFullYear(), today.getMonth(), 0)) }
    case 'proximos30': { const end = new Date(today); end.setDate(today.getDate() + 30); return { inicio: iso(today), fim: iso(end) } }
    case 'ano': return { inicio: iso(new Date(today.getFullYear(), 0, 1)), fim: iso(new Date(today.getFullYear(), 11, 31)) }
    default: return { inicio: '', fim: '' }
  }
}

function PeriodoPicker({ onChange, resetKey }) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState('todos')
  const [cInicio, setCInicio] = useState('')
  const [cFim, setCFim] = useState('')
  const ref = useRef(null)

  useEffect(() => { setSelected('todos'); setCInicio(''); setCFim('') }, [resetKey])
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn)
  }, [])

  const pick = (key) => { setSelected(key); if (key !== 'custom') { const { inicio, fim } = calcRange(key); onChange(inicio, fim); setOpen(false) } }
  const applyCustom = () => { onChange(cInicio, cFim); setOpen(false) }
  const label = PERIODOS.find(p => p.value === selected)?.label || 'Período'
  const ativo = selected !== 'todos'

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm transition-colors outline-none ${ativo ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'}`}>
        <div className="flex items-center gap-2 min-w-0"><Calendar className="w-4 h-4 flex-shrink-0" /><span className="truncate">{label}</span></div>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden w-56">
          {PERIODOS.filter(p => p.value !== 'custom').map(p => (
            <button key={p.value} onClick={() => pick(p.value)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${selected === p.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
              {p.label}{selected === p.value && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          <div className="border-t border-gray-100">
            <button onClick={() => setSelected('custom')}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selected === 'custom' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
              Personalizado
            </button>
            {selected === 'custom' && (
              <div className="px-3 pb-3 space-y-2">
                <input type="date" value={cInicio} onChange={e => setCInicio(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs outline-none" />
                <input type="date" value={cFim} onChange={e => setCFim(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs outline-none" />
                <button onClick={applyCustom} disabled={!cInicio || !cFim} className="w-full py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg disabled:opacity-40">Aplicar</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Upload de Comprovantes ────────────────────────────────────────────────────
function FileUploadArea({ files, onChange }) {
  const inputRef = useRef(null)

  useEffect(() => {
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || [])
      const imageItems = items.filter(i => i.type.startsWith('image/'))
      if (!imageItems.length) return
      const newFiles = imageItems.map((item, idx) => {
        const blob = item.getAsFile()
        return new File([blob], `colado-${Date.now()}-${idx}.png`, { type: 'image/png' })
      })
      onChange(prev => [...prev, ...newFiles])
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [onChange])

  const handleDrop = (e) => { e.preventDefault(); const added = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf'); if (added.length) onChange(prev => [...prev, ...added]) }
  const handleInput = (e) => { const added = Array.from(e.target.files || []); if (added.length) onChange(prev => [...prev, ...added]); e.target.value = '' }
  const remove = (i) => onChange(prev => prev.filter((_, idx) => idx !== i))

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">Comprovantes (opcional)</label>
      <div onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
        <Upload className="w-4 h-4 text-gray-300 mx-auto mb-1" />
        <p className="text-xs text-gray-400">Arraste, clique ou <kbd className="font-mono text-[10px] bg-gray-100 border border-gray-200 rounded px-1">Ctrl+V</kbd></p>
        <p className="text-[11px] text-gray-300 mt-0.5">PDF, PNG, JPG</p>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" onChange={handleInput} className="hidden" />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-xs max-w-[180px]">
              {f.type === 'application/pdf' ? <FileText className="w-3 h-3 text-red-500 flex-shrink-0" /> : <ImageIcon className="w-3 h-3 text-blue-500 flex-shrink-0" />}
              <span className="truncate text-gray-700">{f.name}</span>
              <button onClick={(e) => { e.stopPropagation(); remove(i) }} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modal Comprovantes ────────────────────────────────────────────────────────
function ModalComprovantes({ parcela, orgId, onClose }) {
  const [comprovantes, setComprovantes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    gestaoService.listarComprovantes(orgId, parcela.id)
      .then(r => setComprovantes(r.data || []))
      .catch(() => toast.error('Erro ao carregar comprovantes'))
      .finally(() => setLoading(false))
  }, [orgId, parcela.id])

  const titulo = parcela.vendedor?.nome || parcela.comprador?.nome || `Parcela ${parcela.numero_parcela}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Comprovantes — {titulo}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 min-h-[120px]">
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : comprovantes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum comprovante anexado</p>
          ) : (
            <div className="space-y-3">
              {comprovantes.map(comp => (
                <div key={comp.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  {comp.tipo_arquivo?.startsWith('image/') ? (
                    <img src={comp.url} alt={comp.nome_arquivo} className="w-12 h-12 object-cover rounded-md border border-gray-200 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-md border border-red-100 flex-shrink-0"><FileText className="w-6 h-6 text-red-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{comp.nome_arquivo}</p>
                    <p className="text-xs text-gray-400">{new Date(comp.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a href={comp.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md" title="Visualizar"><ExternalLink className="w-4 h-4" /></a>
                    <a href={comp.url} download={comp.nome_arquivo} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md" title="Baixar"><Download className="w-4 h-4" /></a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Fechar</button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Repasse ─────────────────────────────────────────────────────────────
function ModalRepasse({ vendedor, parcelas, orgId, onClose, onConfirm }) {
  const [dataRepasse, setDataRepasse] = useState(new Date().toISOString().split('T')[0])
  const [observacoes, setObservacoes] = useState('')
  const [formaId, setFormaId] = useState('')
  const [formas, setFormas] = useState([])
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)

  const pendentes = parcelas.filter(p => p.status_repasse !== 'pago')
  const totalValor = pendentes.reduce((s, p) => s + Number(p.valor || 0), 0)

  useEffect(() => {
    gestaoService.listarFormasPagamento(orgId).then(r => setFormas(r.data || r)).catch(() => {})
  }, [orgId])

  const handleConfirm = async () => {
    setLoading(true)
    await onConfirm({
      ids: pendentes.map(p => p.id),
      data_repasse: dataRepasse,
      observacoes_repasse: observacoes || undefined,
      forma_pagamento_repasse_id: formaId ? Number(formaId) : undefined,
    }, files)
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Confirmar Repasse</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm space-y-1 bg-green-50 rounded-lg p-3 border border-green-100">
            <p><span className="font-medium">Vendedor:</span> {vendedor}</p>
            <p><span className="font-medium">Parcelas a repassar:</span> {pendentes.length}</p>
            <p className="text-base font-bold text-gray-900"><span className="font-medium">Total:</span> {fmt(totalValor)}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data do Repasse</label>
            <input type="date" value={dataRepasse} onChange={e => setDataRepasse(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Forma de Pagamento</label>
            <select value={formaId} onChange={e => setFormaId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
              <option value="">Selecione...</option>
              {formas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações (opcional)</label>
            <input type="text" value={observacoes} onChange={e => setObservacoes(e.target.value)}
              placeholder="Ex: Repasse via PIX"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
          </div>
          <FileUploadArea files={files} onChange={setFiles} />
        </div>
        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Cancelar</button>
          <button onClick={handleConfirm} disabled={loading || !dataRepasse || pendentes.length === 0}
            className="flex-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium">
            {loading ? 'Salvando...' : `Repassar ${pendentes.length} parcela${pendentes.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Grupo por Vendedor ────────────────────────────────────────────────────────
function VendedorGroup({ vendedor, parcelas, onRepassarTodos, onEstornar, onVerComprovantes }) {
  const [expanded, setExpanded] = useState(false)

  const totalValor = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalRepassado = parcelas.filter(p => p.status_repasse === 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalPendente = parcelas.filter(p => p.status_repasse !== 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const pendentes = parcelas.filter(p => p.status_repasse !== 'pago')
  const repassados = parcelas.filter(p => p.status_repasse === 'pago').length

  // Sub-agrupa por comprador
  const gruposPorComprador = useMemo(() => {
    const map = {}
    for (const p of parcelas) {
      const nome = p.comprador?.nome || `ID ${p.comprador_id}`
      if (!map[nome]) map[nome] = []
      map[nome].push(p)
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
  }, [parcelas])

  return (
    <tbody>
      <tr className="bg-gray-100 border-t-2 border-gray-300 cursor-pointer select-none" onClick={() => setExpanded(e => !e)}>
        <td colSpan={6} className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-gray-500">
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </span>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700 flex-shrink-0">
                  {vendedor.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider leading-none mb-0.5">Vendedor</p>
                  <span className="font-semibold text-gray-900 text-sm">{vendedor}</span>
                </div>
                <span className="text-xs text-gray-500">({parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''})</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4 text-xs">
                {repassados > 0 && <span className="text-green-600 font-medium">Repassado: {fmt(totalRepassado)}</span>}
                {pendentes.length > 0 && <span className="font-semibold text-amber-700">A repassar: {fmt(totalPendente)}</span>}
                <span className="text-gray-500">Total: {fmt(totalValor)}</span>
              </div>
              {pendentes.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRepassarTodos(vendedor, parcelas) }}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Send className="w-3 h-3" />
                  Repassar
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>
      {expanded && gruposPorComprador.map(([comprador, ps]) => (
        <CompradorSubGroup
          key={comprador}
          comprador={comprador}
          parcelas={ps}
          onRepassarTodos={(_, ps2) => onRepassarTodos(vendedor, ps2)}
          onEstornar={onEstornar}
          onVerComprovantes={onVerComprovantes}
        />
      ))}
    </tbody>
  )
}

// ── Sub-grupo por comprador ───────────────────────────────────────────────────
function CompradorSubGroup({ comprador, parcelas, onRepassarTodos, onEstornar, onVerComprovantes }) {
  const [expanded, setExpanded] = useState(false)
  const pendentes = parcelas.filter(p => p.status_repasse !== 'pago')
  const totalPendente = pendentes.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalRepassado = parcelas.filter(p => p.status_repasse === 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)

  return (
    <>
      <tr className="bg-blue-50/60 border-t border-blue-100">
        <td colSpan={6} className="pl-10 pr-4 py-2">
          <div className="flex items-center justify-between">
            <button onClick={() => setExpanded(e => !e)} className="flex items-center gap-2 text-left">
              <span className="text-gray-400">{expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</span>
              <span className="text-xs font-semibold text-gray-700">{comprador}</span>
              <span className="text-[11px] text-gray-400">({parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''})</span>
            </button>
            <div className="flex items-center gap-3 text-xs">
              {totalRepassado > 0 && <span className="text-green-600">Repassado: {fmt(totalRepassado)}</span>}
              {pendentes.length > 0 && <span className="text-amber-600 font-semibold">Pendente: {fmt(totalPendente)}</span>}
              {pendentes.length > 0 && (
                <button onClick={() => onRepassarTodos(comprador, parcelas)}
                  className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-green-100 text-green-700 border border-green-200 rounded-lg hover:bg-green-200 transition-colors whitespace-nowrap">
                  <Send className="w-3 h-3" />Repassar
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>
      {expanded && parcelas.map(p => (
        <tr key={p.id} className="text-xs border-b border-gray-50 bg-white">
          <td className="pl-14 pr-3 py-2 text-gray-600">
            {p.lote?.numero_lote ? `Lote #${p.lote.numero_lote}` : `ID ${p.lote_id}`}
            {p.total_parcelas > 1 && <span className="ml-1 text-gray-400">{p.numero_parcela}/{p.total_parcelas}</span>}
          </td>
          <td className="px-3 py-2 text-gray-500">{fmtDate(p.data_vencimento)}</td>
          <td className="px-3 py-2 text-center text-gray-500">
            <span className="text-green-600 text-[10px]">Recebido {fmtDate(p.data_pagamento)}</span>
          </td>
          <td className="px-3 py-2 text-right font-semibold text-gray-900">{fmt(p.valor)}</td>
          <td className="px-3 py-2 text-center">
            {p.status_repasse === 'pago' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                <Check className="w-3 h-3" />Repassado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                <Clock className="w-3 h-3" />Pendente
              </span>
            )}
          </td>
          <td className="px-3 py-2 text-right">
            {p.status_repasse === 'pago' ? (
              <div className="flex items-center justify-end gap-2">
                {p.tem_comprovantes && (
                  <button onClick={() => onVerComprovantes(p)}
                    className="p-1 text-purple-400 hover:text-purple-600" title="Comprovantes">
                    <Paperclip className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => onEstornar(p)} className="text-[10px] text-gray-400 hover:text-red-600 underline">estornar</button>
              </div>
            ) : (
              <button onClick={() => onRepassarTodos(comprador, [p])}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <Send className="w-3 h-3" />Repassar
              </button>
            )}
          </td>
        </tr>
      ))}
    </>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Vendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [parcelas, setParcelas] = useState([])
  const [leiloes, setLeiloes] = useState([])
  const [loading, setLoading] = useState(false)
  const [fluxo, setFluxo] = useState(null) // 'direto' | 'via_leiloeira'
  const [loadingConfig, setLoadingConfig] = useState(true)

  // Carregar configuração do fluxo
  useEffect(() => {
    if (!orgId) return
    setLoadingConfig(true)
    gestaoService.obterConfiguracoes(orgId)
      .then(r => setFluxo((r.data || r).fluxo_pagamento || 'direto'))
      .catch(() => setFluxo('direto'))
      .finally(() => setLoadingConfig(false))
  }, [orgId])

  // Filtros
  const [filtroLeilao, setFiltroLeilao] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')
  const [filtroVendedor, setFiltroVendedor] = useState('')
  const [periodoResetKey, setPeriodoResetKey] = useState(0)

  // Modal
  const [modalRepasse, setModalRepasse] = useState(null) // { vendedor, parcelas }
  const [modalComprov, setModalComprov] = useState(null)

  const carregar = useCallback(async () => {
    if (!orgId || !filtroLeilao) return
    setLoading(true)
    try {
      const filtros = {}
      if (filtroLeilao) filtros.leilao_id = filtroLeilao
      if (filtroDataInicio) filtros.data_inicio = filtroDataInicio
      if (filtroDataFim) filtros.data_fim = filtroDataFim
      // Só buscar parcelas onde comprador já pagou
      filtros.status = 'pago'

      const res = await gestaoService.listarParcelasNegociacao(orgId, filtros)
      let data = res.data || res
      // Filtrar por status_repasse no frontend
      if (filtroStatus) {
        data = data.filter(p => p.status_repasse === filtroStatus)
      }
      setParcelas(data)
    } catch {
      toast.error('Erro ao carregar parcelas')
    } finally {
      setLoading(false)
    }
  }, [orgId, filtroLeilao, filtroStatus, filtroDataInicio, filtroDataFim])

  useEffect(() => {
    if (orgId) {
      leiloesService.getByOrganizador(orgId)
        .then(r => {
          const lista = Array.isArray(r) ? r : (r?.data || [])
          const sorted = [...lista].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
          setLeiloes(sorted)
          if (sorted.length > 0) {
            const padrao = sorted.find(l => l.status === 'on') || sorted[0]
            setFiltroLeilao(String(padrao.id))
          }
        })
        .catch(() => {})
    }
  }, [orgId])

  useEffect(() => { carregar() }, [carregar])

  const uploadFiles = async (parcelaIds, files) => {
    if (!files.length) return
    await Promise.allSettled(
      files.map(f => gestaoService.uploadComprovante(orgId, parcelaIds, f))
    )
  }

  const handleRepassarLote = async (dados, files = []) => {
    try {
      const res = await gestaoService.darBaixaRepasseLote(orgId, dados)
      const d = res.data || res
      const ids = d.ids || dados.ids
      await uploadFiles(ids, files)
      toast.success(d.message || 'Repasse realizado com sucesso')
      await carregar()
      setModalRepasse(null)
    } catch {
      toast.error('Erro ao realizar repasse')
    }
  }

  const handleEstornarRepasse = async (parcela) => {
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-800">
          Estornar repasse de <strong>{fmt(parcela.valor)}</strong>?
        </p>
        <p className="text-xs text-gray-500">O repasse será revertido e ficará pendente novamente.</p>
        <div className="flex gap-2 mt-1">
          <button onClick={async () => { toast.dismiss(t.id); try { await gestaoService.estornarRepasse(orgId, parcela.id); toast.success('Estorno de repasse realizado'); await carregar() } catch { toast.error('Erro ao estornar repasse') } }}
            className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">
            Confirmar
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            className="flex-1 px-3 py-1.5 text-xs font-semibold border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </div>
    ), { duration: 15000, style: { maxWidth: '340px' } })
  }

  // Filtra por nome do vendedor
  const parcelasFiltradas = useMemo(() => {
    if (!filtroVendedor.trim()) return parcelas
    const busca = filtroVendedor.toLowerCase()
    return parcelas.filter(p => contem(p.vendedor?.nome, busca))
  }, [parcelas, filtroVendedor])

  // Agrupa por vendedor
  const gruposPorVendedor = useMemo(() => {
    const map = {}
    for (const p of parcelasFiltradas) {
      const nome = p.vendedor?.nome || 'Sem Vendedor'
      if (!map[nome]) map[nome] = []
      map[nome].push(p)
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
  }, [parcelasFiltradas])

  // Totais
  const totalPendente = parcelas.filter(p => p.status_repasse !== 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalRepassado = parcelas.filter(p => p.status_repasse === 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const qtdPendentes = parcelas.filter(p => p.status_repasse !== 'pago').length

  const limparFiltros = () => {
    setFiltroLeilao(''); setFiltroStatus(''); setFiltroDataInicio(''); setFiltroDataFim(''); setFiltroVendedor(''); setPeriodoResetKey(k => k + 1)
  }
  const temFiltroAtivo = filtroLeilao || filtroStatus || filtroDataInicio || filtroDataFim || filtroVendedor

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />Carregando...
      </div>
    )
  }

  if (fluxo === 'direto') {
    return (
      <div className="space-y-4 max-w-2xl">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Repasses ao Vendedor</h1>
          <p className="text-sm text-gray-500 mt-1">Esta funcionalidade está disponível apenas no fluxo "Via Leiloeira".</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-sm text-amber-800 font-medium mb-2">Fluxo atual: Pagamento Direto</p>
          <p className="text-xs text-amber-700">
            No fluxo direto, o comprador paga diretamente ao vendedor. Não há etapa de repasse.
            Para habilitar o controle de repasses, altere o fluxo para "Via Leiloeira" nas{' '}
            <a href="/gestao/configuracoes" className="underline font-semibold hover:text-amber-900">Configurações da Gestão</a>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Repasses ao Vendedor</h1>
          <p className="text-sm text-gray-500">Parcelas recebidas do comprador pendentes de repasse ao vendedor</p>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className={`bg-white rounded-xl border p-4 ${qtdPendentes > 0 ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <Send className={`w-4 h-4 ${qtdPendentes > 0 ? 'text-amber-500' : 'text-gray-400'}`} />
            <span className="text-xs text-gray-500 font-medium">A Repassar</span>
          </div>
          <p className={`text-lg font-bold ${qtdPendentes > 0 ? 'text-amber-700' : 'text-gray-900'}`}>{fmt(totalPendente)}</p>
          <p className="text-xs text-gray-400">{qtdPendentes} parcelas</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-500 font-medium">Repassados</span>
          </div>
          <p className="text-lg font-bold text-green-700">{fmt(totalRepassado)}</p>
          <p className="text-xs text-gray-400">{parcelas.filter(p => p.status_repasse === 'pago').length} parcelas</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500 font-medium">Total Recebido</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{fmt(totalPendente + totalRepassado)}</p>
          <p className="text-xs text-gray-400">{parcelas.length} parcelas</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
          {temFiltroAtivo && (
            <button onClick={limparFiltros} className="ml-auto text-xs text-blue-600 hover:text-blue-800">Limpar filtros</button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select value={filtroLeilao} onChange={e => setFiltroLeilao(e.target.value)}
            className="col-span-2 md:col-span-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none bg-white">
            <option value="">Todos os leilões</option>
            {leiloes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none bg-white">
            <option value="">Todos os status</option>
            <option value="em_aberto">Pendente</option>
            <option value="pago">Repassado</option>
          </select>
          <PeriodoPicker resetKey={periodoResetKey} onChange={(inicio, fim) => { setFiltroDataInicio(inicio); setFiltroDataFim(fim) }} />
          <input type="text" value={filtroVendedor} onChange={e => setFiltroVendedor(e.target.value)}
            placeholder="Buscar vendedor..."
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none" />
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />Carregando repasses...
          </div>
        ) : gruposPorVendedor.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Users className="w-12 h-12 mb-3 opacity-30" />
            <p className="font-medium">Nenhum repasse encontrado</p>
            <p className="text-sm mt-1">Parcelas aparecerão aqui após o comprador efetuar o pagamento.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="pl-10 pr-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Lote</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Vencimento</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Recebido em</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Valor</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Repasse</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Ação</th>
                </tr>
              </thead>
              {gruposPorVendedor.map(([vendedor, ps]) => (
                <VendedorGroup
                  key={vendedor}
                  vendedor={vendedor}
                  parcelas={ps}
                  onRepassarTodos={(nome, parc) => setModalRepasse({ vendedor: nome, parcelas: parc })}
                  onEstornar={handleEstornarRepasse}
                  onVerComprovantes={setModalComprov}
                />
              ))}
            </table>
          </div>
        )}
      </div>

      {/* Modal repasse */}
      {modalRepasse && (
        <ModalRepasse
          vendedor={modalRepasse.vendedor}
          parcelas={modalRepasse.parcelas}
          orgId={orgId}
          onClose={() => setModalRepasse(null)}
          onConfirm={handleRepassarLote}
        />
      )}

      {/* Modal comprovantes */}
      {modalComprov && (
        <ModalComprovantes
          parcela={modalComprov}
          orgId={orgId}
          onClose={() => setModalComprov(null)}
        />
      )}
    </div>
  )
}
